import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, MapPin, Send, ArrowUpRight } from 'lucide-react';
import emailjs from '@emailjs/browser';
import Reveal from './ui/Reveal';

const EMAILJS = {
  serviceId: 'service_qpae7tr',
  templateId: 'template_23o1y3g',
  publicKey: 'u6U4lwtN0WcW-a134',
};

const Contact: React.FC = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({ name: '', email: '', message: '', company: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const contactInfo = [
    {
      icon: Mail,
      title: t('contact.form.email'),
      value: 'anasaljammal686@gmail.com',
      link: 'mailto:anasaljammal686@gmail.com',
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: '+963 997 270 886',
      link: 'https://wa.me/963997270886',
    },
    {
      icon: MapPin,
      title: t('about.facts.location'),
      value: t('contact.info.location'),
      link: undefined,
    },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Honeypot: bots fill hidden fields. Pretend success, send nothing.
    if (formData.company) {
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await emailjs.send(
        EMAILJS.serviceId,
        EMAILJS.templateId,
        { from_name: formData.name, from_email: formData.email, message: formData.message },
        EMAILJS.publicKey
      );

      if (result.status === 200) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', message: '', company: '' });
        setTimeout(() => setIsSubmitted(false), 6000);
      }
    } catch {
      setError(t('contact.form.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section">
      <div className="container-page">
        <Reveal>
          <p className="eyebrow">{t('contact.eyebrow')}</p>
          <h2 className="section-title mt-4 max-w-2xl">{t('contact.title')}</h2>
          <p className="mt-5 max-w-prose text-graphite">{t('contact.description')}</p>
        </Reveal>

        <Reveal className="mt-10 grid gap-10 lg:grid-cols-5 lg:gap-14">
          {/* Details — ruled rows, not cards. Cards mean "a project" on this page. */}
          <div className="lg:col-span-2">
            <dl>
              {contactInfo.map(({ icon: Icon, title, value, link }) => (
                <div key={title} className="row-ruled">
                  <dt className="ui flex items-center gap-2.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-graphite">
                    <Icon size={15} aria-hidden="true" />
                    {title}
                  </dt>
                  <dd className="ui text-[16px] text-ink">
                    {link ? (
                      <a
                        href={link}
                        target={link.startsWith('http') ? '_blank' : undefined}
                        rel={link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="inline-flex min-h-[44px] items-center gap-1.5 break-all transition-colors duration-200 hover:text-lacquer-bright"
                      >
                        {value}
                        <ArrowUpRight size={14} aria-hidden="true" className="shrink-0" />
                      </a>
                    ) : (
                      value
                    )}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 border-t border-hairline pt-8">
              <h3 className="ui flex items-center gap-2.5 text-[15px] font-bold text-ink">
                <span
                  aria-hidden="true"
                  className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-lacquer-bright"
                />
                {t('contact.availabilityTitle')}
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed text-graphite">
                {t('contact.availabilityText')}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {isSubmitted ? (
              /* Success. Previously unstyled; now the same plate the fields sit on,
                 with the accent carrying the confirmation. Text unchanged. */
              <div
                role="status"
                aria-live="polite"
                className="border border-lacquer-bright/40 bg-plate p-8 sm:p-10"
                style={{ borderRadius: 2 }}
              >
                <span
                  aria-hidden="true"
                  className="block h-1 w-12 bg-lacquer-bright"
                  style={{ borderRadius: 2 }}
                />
                <h3 className="ui mt-6 text-[22px] font-bold text-ink">
                  {t('contact.form.sent')}
                </h3>
                <p className="mt-3 max-w-prose text-[16px] leading-relaxed text-graphite">
                  {t('contact.form.successText')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate={false}>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="field-label mb-2.5">
                      {t('contact.form.name')}
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      autoComplete="name"
                      disabled={isSubmitting}
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('contact.form.namePlaceholder')}
                      className="field"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="field-label mb-2.5">
                      {t('contact.form.email')}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      disabled={isSubmitting}
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.form.emailPlaceholder')}
                      className="field"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="message" className="field-label mb-2.5">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={7}
                    disabled={isSubmitting}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t('contact.form.messagePlaceholder')}
                    className="field resize-y"
                  />
                </div>

                {/* Honeypot — hidden from humans */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                {error && (
                  <p
                    role="alert"
                    className="ui mt-6 flex gap-3 border-l-2 border-alert bg-plate p-4 text-[15px] text-ink"
                  >
                    {/* Colour is never the only signal — the message states the problem. */}
                    {error}
                  </p>
                )}

                <button type="submit" disabled={isSubmitting} className="btn-submit mt-8">
                  {isSubmitting ? (
                    <>
                      <span
                        aria-hidden="true"
                        className="h-4 w-4 animate-spin rounded-full border-2 border-paper/40 border-t-paper"
                      />
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      <Send size={18} aria-hidden="true" />
                      {t('contact.form.send')}
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
